import { ExpandMore } from '@material-ui/icons'
import { useLocation } from '@reach/router'
import React, { Fragment, useState } from 'react'
import styled from 'styled-components'

import Link from '../atoms/link'
import theme from '../atoms/theme'

const { breakpoints, colours, animation, font, icon } = theme

interface NavigationItemProps {
  menuItem: {
    label: string
    url: string
    items: Array<{
      label: string
      url: string
    }>
  }
}

export const StyledNavigationItem = styled.li`
  display: flex;
  position: relative;
  z-index: 1;

  @media only screen and ${breakpoints.toNormalScreen} {
    border-bottom: 0.1rem solid ${colours.darkTint1};
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    min-height: 5.4rem;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    &:hover > a,
    &.current > a {
      color: ${colours.primary};
      position: relative;
      &::before {
        display: block;
        content: '';
        height: 0.2rem;
        background-color: ${colours.primary};
        position: absolute;
        top: 2.6rem;
        left: 2rem;
        width: calc(100% - 4rem);
      }
    }

    &:hover .sub-navigation {
      opacity: 1;
      left: 0;
      top: 5.5rem;
      transition: opacity ${animation.defaultLength} ease-in-out, top ${animation.defaultLength} ease-in-out,
        left 0s linear 0s;
    }
  }

  > a {
    display: block;
    color: ${colours.light};
    font-size: ${font.fontSizeNavigationMobile};
    text-decoration: none;
    transition: color ${animation.defaultLength} ease-in-out;

    @media only screen and ${breakpoints.toNormalScreen} {
      flex-grow: 1;
      align-items: center;
      display: flex;
      padding: 0 0 0 2rem;
    }

    @media only screen and ${breakpoints.fromNormalScreen} {
      font-size: ${font.fontSizeDesktop};
      height: 5.5rem;
      padding: 0 2rem;
    }

    &::before {
      display: block;
      content: '';
      height: 0.2rem;
      background-color: ${colours.primary};
      position: absolute;
      top: 2.6rem;
      left: 50%;
      width: 0;
      transition: left ${animation.defaultLength} ease-in-out, width ${animation.defaultLength} ease-in-out;
    }
  }

  .expand {
    transition: transform ${animation.defaultLength} ease-in-out;
    vertical-align: middle;
  }
`

export const StyledExpandMore = styled.div`
  display: flex;
  width: 6.4rem;
  height: 5.4rem;
  float: right;
  position: relative;
  z-index: 10;
  align-items: center;
  transform: ${(props): string => (props.active ? 'rotate(180deg)' : 'rotate(0)')};
  justify-content: center;
  transition: transform ${animation.defaultLength} ease-in-out;

  @media only screen and ${breakpoints.fromNormalScreen} {
    display: none;
  }

  .MuiSvgIcon-root {
    color: ${colours.light};
    width: ${icon.defaultSize};
    height: ${icon.defaultSize};
  }
`

export const SubNavigationItems = styled.nav`
  background-color: ${colours.dark};
  z-index: 10;
  transition: opacity ${animation.defaultLength} ease-in-out, top ${animation.defaultLength} ease-in-out,
    left 0s linear ${animation.defaultLength};
  padding-bottom: 1.2rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    display: ${(props): string => (props.active ? 'block' : 'none')};
    flex-basis: 100%;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    top: 2rem;
    display: block;
    position: absolute;
    left: -999rem;
    opacity: 0;
    overflow: hidden;
    width: 28.2rem;
  }
`

export const SubNavigationItem = styled.div`
  a {
    display: block;
    color: ${colours.light};
    font-size: 1.6rem;
    text-decoration: none;
    padding: 1.4rem 0 1.4rem 2rem;

    @media only screen and ${breakpoints.fromNormalScreen} {
      padding: 1.2rem 2.8rem;
    }

    &:hover {
      color: ${colours.primary};
    }
  }
`

const NavigationItem = (props: NavigationItemProps): JSX.Element => {
  const { menuItem } = props
  const [isActive, setIsActive] = useState(false)
  const location = useLocation()

  function toggleMenuActive(): void {
    event.preventDefault()
    setIsActive(!isActive)
  }

  const menuItems = menuItem.items || []

  const isCurrentChild = menuItems.find((o) => o.url === location.pathname)
  const isCurrent = location.pathname.startsWith(menuItem.url)
  return (
    <StyledNavigationItem
      className={isCurrentChild || isCurrent ? 'current' : null}
    >
      <Link to={menuItem.url}>{menuItem.label}</Link>
      {menuItem.items && menuItem.items.length > 0 && (
        <Fragment>
          <StyledExpandMore
            className='expand'
            onClick={(): void => {
              toggleMenuActive()
            }}
            active={isActive}
          >
            <ExpandMore />
          </StyledExpandMore>
          <SubNavigationItems active={isActive} className='sub-navigation'>
            {menuItem.items.map((subMenuItem) => {
              return (
                <SubNavigationItem
                  key={`${subMenuItem.url}-${subMenuItem.label}`}
                >
                  <Link to={subMenuItem.url}>{subMenuItem.label}</Link>
                </SubNavigationItem>
              )
            })}
          </SubNavigationItems>
        </Fragment>
      )}
    </StyledNavigationItem>
  )
}

export default NavigationItem
